/**
 * TestShotViewer Component
 * Handles Test Shot command flow: request → listen → display with PhotoViewer
 * Phase 2: Remote Test Shot implementation
 *
 * DSLR mode: Shows real-time phases (capturing → transferring → uploading)
 * with smooth progress animation within each phase segment
 * Internal mode: Shows simulated progress bar
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { doc, updateDoc, onSnapshot, Timestamp } from 'firebase/firestore';
import { db } from '@/config/firebase';
import { PhotoViewer } from './PhotoViewer';
import { message, Alert, Button, Progress, Typography } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import type { View, Photo } from '@/types';
import { logError } from '@/utils/logger';
import { useNavigate } from 'react-router-dom';

const { Text } = Typography;

interface TestShotViewerProps {
  view: View;
  onClose: () => void;
}

// Phase labels for DSLR mode (more descriptive)
const PHASE_LABELS: Record<string, string> = {
  capturing: 'Capturing photo from camera...',
  transferring: 'Transferring from camera to phone...',
  uploading: 'Uploading to cloud...',
};

// Error code to user-friendly message mapping (DSLR mode)
const ERROR_MESSAGES: Record<string, string> = {
  DSLR_DISCONNECTED: 'External camera disconnected. Check USB cable connection.',
  CAPTURE_TIMEOUT: 'Camera did not respond. Check focus and exposure settings.',
  CAPTURE_FAILED: 'Failed to capture photo. Try again.',
  TRANSFER_FAILED: 'USB transfer interrupted. Check cable connection.',
  NO_JPEG: 'No photo received from camera. Try again.',
  UPLOAD_TIMEOUT: 'Upload timed out. Check internet connection.',
  UPLOAD_FAILED: 'Failed to upload photo. Check internet connection.',
  SERVICE_RESTART: 'Device restarted during capture. Try again.',
  UNKNOWN: 'An unexpected error occurred. Try again.',
};

// Phase progress ranges and durations for smooth animation
// Each phase animates from startPercent to endPercent over durationMs
const PHASE_CONFIG: Record<string, { startPercent: number; endPercent: number; durationMs: number }> = {
  capturing: { startPercent: 0, endPercent: 33, durationMs: 45000 },    // 45s for long exposures
  transferring: { startPercent: 33, endPercent: 66, durationMs: 15000 }, // 15s for USB transfer
  uploading: { startPercent: 66, endPercent: 100, durationMs: 15000 },   // 15s for cloud upload
};

/**
 * TestShotViewer manages the complete Test Shot flow:
 * 1. Write command to Firestore (status: pending)
 * 2. Listen for command updates via onSnapshot
 * 3. Handle timeout (30s if device doesn't respond)
 * 4. Display result with PhotoViewer (loading → photo → error)
 */
export function TestShotViewer({ view, onClose }: TestShotViewerProps) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [testShotPhoto, setTestShotPhoto] = useState<Photo | null>(null);
  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState<string | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const commandRequestedAtRef = useRef<Timestamp | null>(null);
  const phaseStartTimeRef = useRef<number>(0);
  const phaseStartProgressRef = useRef<number>(0);

  const cameraMode = view.settings?.camera?.mode ?? 'internal';
  const dslrModel = view.androidStatus?.device?.dslr?.model;
  const isDslr = cameraMode === 'dslr';

  // Progress bar animation for Internal camera mode only
  useEffect(() => {
    if (!isDslr && loading && !testShotPhoto) {
      setProgress(0);
      progressIntervalRef.current = setInterval(() => {
        setProgress(prev => Math.min(prev + 1, 100));
      }, 300); // 30s for internal camera
    } else if (!isDslr) {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
    }
    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, [isDslr, loading, testShotPhoto]);

  // Smooth progress animation for DSLR phases
  useEffect(() => {
    if (!isDslr || !phase || !loading) {
      return;
    }

    const config = PHASE_CONFIG[phase];
    if (!config) {
      return;
    }

    // Clear any existing interval
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }

    // Record start time and progress for this phase
    phaseStartTimeRef.current = Date.now();
    phaseStartProgressRef.current = progress;

    // If current progress is already past this phase's start, jump to start
    if (progress < config.startPercent) {
      setProgress(config.startPercent);
      phaseStartProgressRef.current = config.startPercent;
    }

    // Animate progress within this phase segment
    const updateInterval = 100; // Update every 100ms for smooth animation
    const progressRange = config.endPercent - config.startPercent;

    progressIntervalRef.current = setInterval(() => {
      const elapsed = Date.now() - phaseStartTimeRef.current;
      const targetProgress = Math.min(
        config.startPercent + (elapsed / config.durationMs) * progressRange,
        config.endPercent
      );
      setProgress(Math.round(targetProgress));
    }, updateInterval);

    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, [isDslr, phase, loading]);

  // Send Test Shot command to Firestore
  const sendTestShotCommand = useCallback(async () => {
    try {
      const viewRef = doc(db, `users/${view.userId}/projects/${view.projectId}/views/${view.viewId}`);
      const requestedAt = Timestamp.now();

      // Store the timestamp to verify responses match our request
      commandRequestedAtRef.current = requestedAt;

      await updateDoc(viewRef, {
        'commands.testShot': {
          requestedAt,
          status: 'pending',
        },
      });

      // Reset state for new command
      setLoading(true);
      setError(null);
      setTestShotPhoto(null);
      setPhase(null);
      setProgress(0);

      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Set initial timeout (30s) - cancelled when device responds with a phase
      timeoutRef.current = setTimeout(() => {
        setLoading(false);
        setError('Device not responding');
        message.error('Device not responding. Please check device status.');
      }, 30000);
    } catch (err) {
      logError('Failed to send test shot command', err, { viewId: view.viewId });
      message.error('Failed to send test shot command');
      setLoading(false);
      setError('Failed to send command');
    }
  }, [view]);

  // Listen to command status changes
  useEffect(() => {
    const viewRef = doc(db, `users/${view.userId}/projects/${view.projectId}/views/${view.viewId}`);

    const unsubscribe = onSnapshot(
      viewRef,
      (snapshot) => {
        const data = snapshot.data();
        const command = data?.commands?.testShot;

        console.log('[TestShotViewer] Firestore snapshot update:', {
          hasCommand: !!command,
          command: command,
        });

        if (!command) {
          console.log('[TestShotViewer] No command found in snapshot');
          return;
        }

        console.log('[TestShotViewer] Command status:', command.status, 'phase:', command.phase);

        // Always verify timestamp matches our request
        const ourRequestedAt = commandRequestedAtRef.current;
        const responseRequestedAt = command.requestedAt;

        // Skip if no timestamp yet (command not sent) or timestamps don't match
        if (!ourRequestedAt || !responseRequestedAt) {
          console.log('[TestShotViewer] Waiting for our command to be written...');
          return;
        }

        const ourMs = ourRequestedAt.toMillis();
        const responseMs = responseRequestedAt.toMillis();

        // Skip if this is not our command (exact match required)
        if (ourMs !== responseMs) {
          console.log('[TestShotViewer] Ignoring response from different command:', {
            ourRequestedAt: ourMs,
            responseRequestedAt: responseMs,
          });
          return;
        }

        // Handle phase updates (DSLR mode only)
        if (command.phase && isDslr) {
          console.log('[TestShotViewer] Phase update:', command.phase);

          // Only update phase if it's a new phase (not the same)
          if (command.phase !== phase) {
            setPhase(command.phase);

            // Jump to the start of the new phase segment
            const config = PHASE_CONFIG[command.phase];
            if (config) {
              setProgress(config.startPercent);
            }
          }

          // Cancel timeout - device is responding
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
          }
        }

        if (command.status === 'pending') {
          console.log('[TestShotViewer] Our command is pending, waiting for device...');
        } else if (command.status === 'completed' && command.url) {

          console.log('[TestShotViewer] Command completed:', {
            status: command.status,
            url: command.url,
            metadata: command.metadata,
            capturedAt: command.capturedAt,
          });

          // Clear timeout and progress interval
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
          }
          if (progressIntervalRef.current) {
            clearInterval(progressIntervalRef.current);
            progressIntervalRef.current = null;
          }

          // Set progress to 100% on completion
          setProgress(100);

          // Convert test shot to Photo format for PhotoViewer
          const meta = command.metadata;
          const photo: Photo = {
            photoId: 'test-shot',
            userId: view.userId,
            projectId: view.projectId,
            viewId: view.viewId,
            capturedAt: command.capturedAt || Timestamp.now(),
            uploadedAt: command.capturedAt || Timestamp.now(),
            urls: {
              full: command.url,
              thumb200: command.url,
            },
            exif: meta ? {
              iso: meta.iso,
              shutterSpeed: meta.shutterSpeed,
              whiteBalance: '',
              width: 0,
              height: 0,
            } : undefined,
            settings: meta ? {
              camera: (meta.camera as 'wide' | 'normal' | 'tele') || 'wide',
              focusMode: (meta.focusMode as 'auto' | 'infinity') || 'auto',
              evCompensation: meta.evCompensation || 0,
              hdrMode: (meta.hdrMode as 'off' | 'low' | 'medium' | 'strong') || 'off',
            } : undefined,
          };

          console.log('[TestShotViewer] Photo object created:', photo);
          setTestShotPhoto(photo);
          setLoading(false);
          setError(null);
        } else if (command.status === 'error') {
          // Clear timeout
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
          }
          if (progressIntervalRef.current) {
            clearInterval(progressIntervalRef.current);
            progressIntervalRef.current = null;
          }

          // Map error code to user-friendly message (fallback to raw error)
          const errorCode = command.errorCode as string | undefined;
          const userMessage = errorCode && ERROR_MESSAGES[errorCode]
            ? ERROR_MESSAGES[errorCode]
            : command.error || 'Test shot failed';

          console.log('[TestShotViewer] Error:', { errorCode, rawError: command.error, userMessage });

          setLoading(false);
          setError(userMessage);
          message.error(userMessage);
        }
      },
      (err) => {
        logError('Error listening to test shot command', err, { viewId: view.viewId });
        setLoading(false);
        setError('Connection error');
      }
    );

    return () => {
      unsubscribe();
    };
  }, [view, isDslr, phase]);

  // Send command on mount (only once)
  useEffect(() => {
    sendTestShotCommand();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - run only once on mount

  // Handle close
  const handleClose = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
    }
    onClose();
  };

  // Get display message based on mode and phase
  const getLoadingMessage = () => {
    if (isDslr) {
      if (phase && PHASE_LABELS[phase]) {
        return PHASE_LABELS[phase];
      }
      return `Connecting to ${dslrModel || 'external camera'}...`;
    }
    return 'Capturing photo from internal camera';
  };

  // Render loading state with progress bar
  if (loading && !testShotPhoto) {
    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.95)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}
      >
        <div style={{ width: 280, textAlign: 'center' }}>
          <Progress
            percent={progress}
            strokeColor="#1890ff"
            trailColor="rgba(255,255,255,0.2)"
            showInfo={false}
            size={['100%', 8]}
          />
          <Text style={{ color: '#ffffff', marginTop: 16, display: 'block' }}>
            {getLoadingMessage()}
          </Text>
          {isDslr && !phase && (
            <Text style={{ color: '#999', marginTop: 8, display: 'block', fontSize: 12 }}>
              Waiting for device response...
            </Text>
          )}
        </div>
        <Button
          type="text"
          icon={<CloseOutlined />}
          onClick={handleClose}
          style={{
            position: 'absolute',
            top: 16,
            right: 16,
            color: '#ffffff',
            fontSize: 20,
          }}
        />
      </div>
    );
  }

  // Render error state
  if (error && !testShotPhoto) {
    return (
      <PhotoViewer
        photo={{
          photoId: 'error',
          userId: view.userId,
          projectId: view.projectId,
          viewId: view.viewId,
          capturedAt: Timestamp.now(),
          uploadedAt: Timestamp.now(),
          urls: {
            full: '',
            thumb200: '',
          },
        }}
        initialLoading={false}
        showFullMetadata={true}
        onClose={handleClose}
        extraActions={
          <Alert
            message={error}
            type="error"
            showIcon
            action={
              <Button
                size="small"
                onClick={() => navigate('/projects')}
              >
                Check Device Status
              </Button>
            }
          />
        }
      />
    );
  }

  // Render photo
  if (testShotPhoto) {
    return (
      <PhotoViewer
        photo={testShotPhoto}
        showFullMetadata={true}
        onClose={handleClose}
      />
    );
  }

  return null;
}
